// The positioning shared by every full-screen overlay (dialogs, image zooms).
//
// The backdrop still covers the whole window — the sidebar dims with the rest — but
// the content centres in the app's content area, not in the window. Without the left
// padding the desktop rail (AppSidebar, `w-56` from `md:` up) pushes every dialog
// off-centre to the left. 15rem = the rail's 14rem plus the overlay's own 1rem.
//
// Callers add their own backdrop tint (and cursor, for the zoom overlays).

// THE INSET ITSELF, on its own line, because two things now have to agree about it: the
// overlay, which centres its content inside this padding, and anything positioned ABSOLUTELY
// against the overlay — which is positioned against the padding BOX and so lands 7.5rem left
// of everything laid out inside it. The picture zoom's "3 / 9" pill was exactly that: centred
// on the window while the photograph above it was centred on the content area. Written once,
// applied to a full-size flex layer, they cannot disagree again.
export const overlayInsetCls = 'p-4 md:pl-60';

export const overlayCls = `fixed inset-0 z-50 flex items-center justify-center ${overlayInsetCls}`;

// THE SAME, MINUS THE RAIL'S SHARE. For an overlay that fills the window rather than
// sitting in it: a near-opaque scrim with a frame as wide as the screen allows.
//
// The reasoning above stops applying exactly there. Reserving 15rem centres a dialog
// against the page you were reading — which you can still see, dimmed, around it. When the
// scrim is black/80 and the frame is 95vw there is no page left to centre against, so the
// padding is simply 15rem of empty screen on the left and the frame pushed 7.5rem right of
// the middle: off-centre against the only thing still visible, the window. It also steals
// the width that decides whether the panels fit on one row.
export const wideOverlayCls = 'fixed inset-0 z-50 flex items-center justify-center p-4';

// AND THE ONE THAT GOES OVER EVERYTHING, for zooming a picture.
//
// A zoom is always ABOUT something already on screen — a thumbnail in a dialog, a render
// inside a prompt preview — so it is the one overlay that is never the thing underneath.
// At z-50 among a page of z-50s it lands wherever the DOM order happens to put it, which
// is how a zoomed picture came to open behind the modal whose strip it was clicked in.
//
// The height alone is not enough: an overlay nested inside a dialog's own z-50 cannot
// paint above the dialog's siblings whatever its z-index, because the dialog is a stacking
// context and everything inside it is confined. That is why ImageLightbox portals to the
// body — the two go together, and neither works alone.
export const zoomOverlayCls = `fixed inset-0 z-[60] flex items-center justify-center ${overlayInsetCls}`;

// THE LOOK every dialog shares, taken from the 3D model's (MeshModal): a dimmed, blurred
// backdrop and ONE white card that holds everything — the content, its corner badge, its
// close button, its controls. Nothing floats bare on the backdrop: a page or a picture with
// its labels and buttons scattered over black read as a screenshot, not as a dialog.
export const modalBackdropCls = 'bg-black/70 backdrop-blur-sm';
export const modalCardCls =
  'rounded-2xl border border-slate-200 bg-white text-slate-700 shadow-2xl';
// The close button, in the card's top-right corner (the card is `relative`).
export const modalCloseCls =
  'absolute right-3 top-3 z-10 rounded-lg p-1 text-slate-400 transition-colors hover:text-slate-700';
